import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Download, Upload, ArrowLeft, ArrowRight, Loader2, Check, AlertTriangle, FileSpreadsheet,
} from "lucide-react";

/**
 * 4-step product bulk-upload wizard.
 *   1. Download sample template
 *   2. Upload file + (auto) parse → suggested column mapping
 *   3. Tweak column mapping + add hardcoded fixed values for every row
 *   4. Preview → Commit. Result shows created/failed counts + log link.
 */
export default function AdminBulkUpload() {
  const [step, setStep] = useState(1);
  const [fields, setFields] = useState([]);
  const [requiredKeys, setRequiredKeys] = useState([]);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);   // {headers, suggested_mapping, preview_rows, ...}
  const [mapping, setMapping] = useState({});             // {field_key: header_name}
  const [hardcoded, setHardcoded] = useState({});         // {field_key: value}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);

  // Load schema on mount
  useEffect(() => {
    api.get("/admin/products/bulk-upload/schema").then(({ data }) => {
      setFields(data.fields || []);
      setRequiredKeys(data.required || []);
    });
    api.get("/admin/products/bulk-upload/logs?limit=10").then(({ data }) => {
      setLogs(data.items || []);
    });
  }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/bulk-upload/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "product-upload-template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const runPreview = async (selectedFile, mappingOverride = null, hardcodedOverride = null) => {
    const fd = new FormData();
    fd.append("file", selectedFile);
    fd.append("mapping", JSON.stringify(mappingOverride ?? mapping));
    fd.append("hardcoded", JSON.stringify(hardcodedOverride ?? hardcoded));
    const { data } = await api.post("/admin/products/bulk-upload/preview", fd);
    return data;
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setSubmitting(true);
    try {
      const data = await runPreview(f, {}, {});
      setPreviewData(data);
      setMapping(data.effective_mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't parse file");
    } finally {
      setSubmitting(false);
    }
  };

  const refreshPreview = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const data = await runPreview(file);
      setPreviewData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setSubmitting(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("hardcoded", JSON.stringify(hardcoded));
      const { data } = await api.post("/admin/products/bulk-upload/commit", fd);
      setResult(data);
      setStep(4);
      toast.success(`Imported ${data.created} of ${data.total_rows}`);
      // Refresh logs sidebar
      const lg = await api.get("/admin/products/bulk-upload/logs?limit=10");
      setLogs(lg.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="bulk-upload-page" className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
      <div>
        <div className="overline mb-1">Catalog</div>
        <h1 className="font-display text-3xl md:text-4xl mb-6">Bulk Upload Products</h1>

        <StepRail step={step} />

        {/* STEP 1 — Template */}
        {step === 1 && (
          <section className="bg-white border border-[hsl(var(--kb-border))] p-6 mt-6" data-testid="bulk-step-1">
            <h2 className="font-display text-xl mb-3">1. Start with the sample template</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))] mb-4 max-w-xl">
              The sample includes every column the importer understands, with two filled-in example rows
              and a "How to use" tab. You don't have to use exact column names — you can map your own
              in the next step.
            </p>
            <button
              type="button"
              onClick={downloadTemplate}
              className="kb-btn-primary inline-flex items-center gap-2"
              data-testid="bulk-download-template"
            >
              <Download size={14} /> Download sample template (.xlsx)
            </button>

            <div className="border-t border-dashed border-[hsl(var(--kb-border))] my-6" />

            <h2 className="font-display text-xl mb-3">2. Upload your filled file</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))] mb-4">
              Accepts .xlsx or .csv. Max recommended: 500 rows per upload.
            </p>
            <label className="kb-btn-outline inline-flex items-center gap-2 cursor-pointer" data-testid="bulk-file-label">
              <Upload size={14} /> Choose file
              <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
            </label>
            {submitting && <div className="mt-3 text-sm text-[hsl(var(--kb-muted))] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Parsing…</div>}
          </section>
        )}

        {/* STEP 2 — Mapping */}
        {step === 2 && previewData && (
          <section className="bg-white border border-[hsl(var(--kb-border))] p-6 mt-6" data-testid="bulk-step-2">
            <h2 className="font-display text-xl mb-2">Map your columns</h2>
            <p className="text-sm text-[hsl(var(--kb-muted))] mb-4 max-w-2xl">
              We auto-detected {Object.keys(previewData.suggested_mapping || {}).length} of {fields.length} columns
              from your file. Adjust any mismatches below.
              Detected {previewData.total_rows} data rows.
            </p>
            <div className="space-y-2 max-h-96 overflow-auto pr-2" data-testid="bulk-mapping-list">
              {fields.map((f) => (
                <div key={f.key} className="grid grid-cols-[160px_1fr_60px] items-center gap-3 text-sm">
                  <div className="font-medium">
                    {f.label}
                    {f.required && <span className="text-red-600 ml-1">*</span>}
                  </div>
                  <select
                    className="kb-input"
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value || undefined })}
                    data-testid={`bulk-map-${f.key}`}
                  >
                    <option value="">— skip —</option>
                    {(previewData.headers || []).map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <span className="text-[10px] text-[hsl(var(--kb-muted))] uppercase tracking-wider">{f.type}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setStep(1)} className="kb-btn-outline inline-flex items-center gap-1" data-testid="bulk-back-1">
                <ArrowLeft size={12} /> Back
              </button>
              <button type="button" onClick={() => setStep(3)} className="kb-btn-primary inline-flex items-center gap-1" data-testid="bulk-next-3">
                Hardcode fields <ArrowRight size={12} />
              </button>
            </div>
          </section>
        )}

        {/* STEP 3 — Hardcoded fields + preview + commit */}
        {step === 3 && previewData && (
          <section className="bg-white border border-[hsl(var(--kb-border))] p-6 mt-6 space-y-6" data-testid="bulk-step-3">
            <div>
              <h2 className="font-display text-xl mb-2">Hardcode shared values (optional)</h2>
              <p className="text-sm text-[hsl(var(--kb-muted))] mb-4 max-w-2xl">
                Want every row to share a value (e.g. all rows are status=draft, or all rows are
                brand=Indira)? Enter it here and we'll override the column for every row.
              </p>
              <div className="grid md:grid-cols-2 gap-3" data-testid="bulk-hardcode-grid">
                {fields.map((f) => (
                  <label key={f.key} className="text-xs">
                    <div className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">{f.label}</div>
                    <input
                      className="kb-input"
                      placeholder="(leave blank to use uploaded value)"
                      value={hardcoded[f.key] ?? ""}
                      onChange={(e) => setHardcoded({ ...hardcoded, [f.key]: e.target.value })}
                      data-testid={`bulk-hard-${f.key}`}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Preview (first 50 rows)</h3>
                <button type="button" onClick={refreshPreview} className="text-xs underline text-[hsl(var(--kb-muted))]" data-testid="bulk-refresh-preview">
                  Refresh preview
                </button>
              </div>
              <div className="flex gap-3 text-xs mb-3">
                <span className="text-green-700 inline-flex items-center gap-1" data-testid="bulk-preview-valid">
                  <Check size={12} /> {previewData.valid_count} valid
                </span>
                <span className="text-red-700 inline-flex items-center gap-1" data-testid="bulk-preview-invalid">
                  <AlertTriangle size={12} /> {previewData.invalid_count} invalid
                </span>
              </div>
              <div className="border border-[hsl(var(--kb-border))] max-h-72 overflow-auto" data-testid="bulk-preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(var(--kb-surface-alt))] sticky top-0">
                    <tr>
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Title</th>
                      <th className="p-2 text-left">Price</th>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview_rows.map((r) => (
                      <tr key={r.row_number} className={r.error ? "bg-red-50" : ""}>
                        <td className="p-2 align-top">{r.row_number}</td>
                        <td className="p-2 align-top">
                          {r.normalized?.title || "—"}
                          {r.error && <div className="text-red-700 text-[10px] mt-1">{r.error}</div>}
                        </td>
                        <td className="p-2 align-top">{r.normalized?.price ?? "—"}</td>
                        <td className="p-2 align-top">{r.normalized?.category_slug ?? "—"}</td>
                        <td className="p-2 align-top">{r.normalized?.status ?? "draft"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between gap-2">
              <button type="button" onClick={() => setStep(2)} className="kb-btn-outline inline-flex items-center gap-1" data-testid="bulk-back-2">
                <ArrowLeft size={12} /> Mapping
              </button>
              <button
                type="button"
                disabled={submitting || previewData.valid_count === 0}
                onClick={commit}
                className="kb-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="bulk-commit-btn"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Import {previewData.valid_count} product(s)
              </button>
            </div>
          </section>
        )}

        {/* STEP 4 — Result */}
        {step === 4 && result && (
          <section className="bg-white border border-[hsl(var(--kb-border))] p-6 mt-6" data-testid="bulk-step-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-50 text-green-700 rounded-full flex items-center justify-center">
                <Check size={22} />
              </div>
              <div>
                <h2 className="font-display text-xl">Import complete</h2>
                <p className="text-sm text-[hsl(var(--kb-muted))]">
                  {result.created} of {result.total_rows} rows created · {result.failed} failed
                </p>
              </div>
            </div>
            {result.failed > 0 && (
              <div className="border border-red-200 bg-red-50 p-3 text-xs mb-4" data-testid="bulk-result-failures">
                <div className="font-medium text-red-700 mb-1">Failed rows</div>
                <ul className="space-y-1">
                  {result.results.filter((r) => r.status === "failed").slice(0, 10).map((r) => (
                    <li key={r.row_number}>Row {r.row_number}: {r.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Link to={`/admin/products`} className="kb-btn-primary inline-flex items-center gap-1" data-testid="bulk-go-products">
                View products
              </Link>
              <Link to={`/admin/products/bulk-upload/${result.import_log_id}`} className="kb-btn-outline" data-testid="bulk-view-log">
                Open full log
              </Link>
              <button type="button" onClick={() => { setStep(1); setFile(null); setResult(null); setPreviewData(null); }} className="kb-btn-outline" data-testid="bulk-restart">
                Upload another
              </button>
            </div>
          </section>
        )}
      </div>

      {/* RIGHT RAIL — past uploads */}
      <aside className="bg-white border border-[hsl(var(--kb-border))] p-5 lg:sticky lg:top-6" data-testid="bulk-past-uploads">
        <h3 className="font-display text-lg flex items-center gap-2 mb-3"><FileSpreadsheet size={16} /> Past uploads</h3>
        {logs.length === 0 ? (
          <p className="text-sm text-[hsl(var(--kb-muted))]">No uploads yet.</p>
        ) : (
          <ul className="space-y-2">
            {logs.map((l) => (
              <li key={l.id}>
                <Link
                  to={`/admin/products/bulk-upload/${l.id}`}
                  className="block p-2 hover:bg-[hsl(var(--kb-surface-alt))] text-sm"
                  data-testid={`bulk-log-${l.id}`}
                >
                  <div className="truncate font-medium">{l.filename || "upload"}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">
                    {l.created_count}/{l.total_rows} created · {l.failed_count} failed
                  </div>
                  <div className="text-[10px] text-[hsl(var(--kb-muted))]">{new Date(l.started_at).toLocaleString()}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}

function StepRail({ step }) {
  const steps = ["Template & file", "Map columns", "Hardcode & preview", "Result"];
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="bulk-step-rail">
      {steps.map((label, i) => {
        const num = i + 1;
        const active = step >= num;
        const current = step === num;
        return (
          <React.Fragment key={label}>
            <span className={`flex items-center gap-1 ${active ? "text-[hsl(var(--kb-primary))]" : "text-[hsl(var(--kb-muted))]"} ${current ? "font-medium" : ""}`}>
              <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] ${active ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-[hsl(var(--kb-surface-alt))]"}`}>{num}</span>
              {label}
            </span>
            {i < steps.length - 1 && <span className="text-[hsl(var(--kb-muted))]">·</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
}
